import { CameraFeed } from "@/components/_module/faceAuth/CameraFeed.tsx";
import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useToast } from "@/components/ui/use-toast.ts";
import { useFaceRegistration } from "@/features/face-registration/useFaceRegistration.ts";
import { useAuth } from "@/context/auth-context.tsx";
import ErrorPage from "@/components/ui/ErrorPage.tsx";
import { requestProps } from "@/api/face-registration.ts";
import WhiteLabelLogo from "@/components/_module/loading/white-label-logo";
import Typography from "@/components/ui/typography.tsx";
import SuccessScreen from "@/components/ui/SuccessScreen.tsx";
import { buttonVariants } from "@/components/ui/button/button-variants.ts";
import { cn } from "@/lib/utils.ts";
import { useQueryClient } from "@tanstack/react-query";
import { userKeys } from "@/features/auth/keys";
import { CameraOverlay } from "@/components/_module/faceAuth/CameraOverlay";
import { useCamera, ImageQuality, CropAreas } from "@/common/hooks/useCamera";

export default function FaceRegistration() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const faceRegistrationMutation = useFaceRegistration();
	const guestInfo = useAuth();
	const queryClient = useQueryClient();
	const [showTerminalErrorScreen, toggleTerminalErrorScreen] =
		useState<boolean>(false);

	const { videoRef, captureImage } = useCamera(
		() => toggleTerminalErrorScreen(true),
		{
			quality: ImageQuality.high,
			cropArea: CropAreas.portrait,
			maxWidth: 720,
		},
	);

	const processPicture = useCallback(
		(blob: Blob | null) => {
			if (blob === null) {
				toast({
					title: "Error",
					description: "Failed to capture photo. Please try again.",
					variant: "destructive",
				});
				return;
			}

			const registrationId = guestInfo.user.location_registration?.id;
			if (registrationId === null || registrationId === undefined) {
				toggleTerminalErrorScreen(true);
				return;
			}

			const payload: requestProps = {
				guest_id: guestInfo.user.id,
				imageBlob: blob,
				guest_registration_id: registrationId,
			};

			faceRegistrationMutation.mutate(payload, {
				onSuccess: () => {
					queryClient.refetchQueries({
						queryKey: userKeys.userInformation(),
					});
				},
				onError: (error) => {
					toast({
						title: "Error",
						description: error.message,
						variant: "destructive",
					});
				},
			});
		},
		[guestInfo.user.id, guestInfo.user.location_registration?.id],
	);

	const handleCapture = async () => {
		try {
			const blob = await captureImage();
			processPicture(blob);
		} catch (error) {
			console.error("Capture failed:", error);
			toast({
				title: "Error",
				description: "Failed to capture photo. Please try again.",
				variant: "destructive",
			});
		}
	};

	if (showTerminalErrorScreen) {
		return (
			<ErrorPage
				message="Failed to find necessary guest data"
				submitText="Back to Home"
				onClose={() => navigate({ to: "/main/home" })}
				onSubmit={() => navigate({ to: "/main/home" })}
			/>
		);
	}

	return (
		<div className="relative w-full h-dvh bg-black overflow-hidden">
			<CameraOverlay
				isLoading={faceRegistrationMutation.isPending}
				onBackClick={() => navigate({ to: "/main/home" })}
				onCaptureClick={handleCapture}
			>
				<CameraFeed videoRef={videoRef} />
			</CameraOverlay>

			{faceRegistrationMutation.status === "pending" && (
				<div className="absolute inset-0 z-50 flex flex-col text-center items-center justify-center bg-background">
					<WhiteLabelLogo size={100} />
					<Typography variant={"h5"} className="text-primary">
						Registering Your Face ID
					</Typography>
				</div>
			)}

			{faceRegistrationMutation.status === "success" && (
				<div className="absolute inset-0 z-50 bg-background">
					<SuccessScreen
						title="Your Face ID has been successfully registered"
						description="You can now start authenticating with Face ID."
						onClose={() => {
							navigate({ to: "/main/home" });
						}}
					>
						<Link
							to={"/main/home"}
							className={cn(buttonVariants(), "min-w-48")}
						>
							Go Home
						</Link>
					</SuccessScreen>
				</div>
			)}
		</div>
	);
}
